/**
 * This file is part of Discorch.
 * Copyright (C) 2025 Discorch Contributors.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { Message } from "@/types";
import { create } from "zustand";

interface SelectionState {
  selectedMessages: Map<string, Set<string>>;

  toggleMessage: (channelId: string, messageId: string) => void;
  toggleChannel: (
    channelId: string,
    messages: Message[],
    selected: boolean,
  ) => void;
  isMessageSelected: (channelId: string, messageId: string) => boolean;
  getChannelSelectionState: (
    channelId: string,
    totalMessages: number,
  ) => {
    selected: boolean;
    indeterminate: boolean;
    count: number;
  };
  clearSelection: () => void;
}

export const useSelection = create<SelectionState>()((set, get) => ({
  selectedMessages: new Map(),

  toggleMessage: (channelId: string, messageId: string) => {
    set((state) => {
      const newMap = new Map(state.selectedMessages);
      const channelMessages = new Set(newMap.get(channelId) || []);

      if (channelMessages.has(messageId)) {
        channelMessages.delete(messageId);
      } else {
        channelMessages.add(messageId);
      }

      if (channelMessages.size === 0) {
        newMap.delete(channelId);
      } else {
        newMap.set(channelId, channelMessages);
      }

      return { selectedMessages: newMap };
    });
  },

  toggleChannel: (
    channelId: string,
    messages: Message[],
    selected: boolean,
  ) => {
    set((state) => {
      const newMap = new Map(state.selectedMessages);

      if (selected) {
        newMap.set(channelId, new Set(messages.map((msg) => msg.ID)));
      } else {
        newMap.delete(channelId);
      }

      return { selectedMessages: newMap };
    });
  },

  isMessageSelected: (channelId: string, messageId: string) => {
    const state = get();
    return state.selectedMessages.get(channelId)?.has(messageId) || false;
  },

  getChannelSelectionState: (channelId: string, totalMessages: number) => {
    const state = get();
    const selectedCount = state.selectedMessages.get(channelId)?.size || 0;

    return {
      selected: selectedCount === totalMessages && totalMessages > 0,
      indeterminate: selectedCount > 0 && selectedCount < totalMessages,
      count: selectedCount,
    };
  },

  clearSelection: () => {
    set({ selectedMessages: new Map() });
  },
}));
